import { useCallback } from 'react';

import { largeUploadByIdSelector, largeUploadUrlInfoSelector, type Upload } from '@air/redux-uploader';
import { useCreateLargeUpload } from '@air/upload-utils';

import { type PrivateUploadTaskParams } from '~/components/Upload/hooks/types';
import { usePrepareForPrivateUploading } from '~/components/Upload/privateUpload/usePrepareForPrivateUploading';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { usePrivateUploadUrlsManager } from '~/providers/PrivateUploadUrlsManagerProvider';
import { useAirStore } from '~/utils/ReduxUtils';

type CreateLargePrivateUploadParams = {
  uploadId: Upload['id'];
  onUploadStarted: PrivateUploadTaskParams['onUploadStarted'];
  onBoardCreated?: PrivateUploadTaskParams['onBoardCreated'];
  onError: PrivateUploadTaskParams['onError'];
};

export const useCreateLargePrivateUpload = () => {
  const store = useAirStore();
  const { createLargeUpload } = useCreateLargeUpload();
  const { prepareForUploading } = usePrepareForPrivateUploading();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { manager: uploadUrlsManager } = usePrivateUploadUrlsManager();

  const createLargePrivateUpload = useCallback(
    async ({ onBoardCreated, onUploadStarted, uploadId, onError }: CreateLargePrivateUploadParams) => {
      const upload = largeUploadByIdSelector(store.getState(), uploadId);
      if (upload) {
        await createLargeUpload({
          getLargeUploadUrlInfo: async () => {
            if (!workspaceId) {
              throw new Error('No workspace id');
            }

            await prepareForUploading({ upload, onBoardCreated });
            const uploadUrlInfo = largeUploadUrlInfoSelector(store.getState(), uploadId);
            return uploadUrlInfo ?? (await uploadUrlsManager.getLargeUploadUrl(uploadId));
          },
          onError,
          upload,
          onUploadStarted,
        });
      }
    },
    [createLargeUpload, prepareForUploading, store, workspaceId, uploadUrlsManager.getLargeUploadUrl],
  );

  return {
    createLargePrivateUpload,
  };
};
